import { AxiosError } from 'axios'
import { useCallback } from 'react'
import { type QueryObserverOptions, useQuery } from 'react-query'
import { useParams } from 'react-router'

import { getOptionalServiceQueryKeyParams } from 'components/services/service-utils'
import useAuth from 'hooks/use-auth'
import { reliabilityTestsQueryKeys } from 'queries/reliability-tests/query-keys'
import {
  fetchReliabilityTestRunsById,
  selectReliabilityTestRunByNumber
} from 'queries/reliability-tests/query-utils'

import type { ReliabilityTestRun } from 'types/api/reliability-tests/runs'

/**
 * Fetches all RM test runs by RM test id, and also selects the run which matches the
 * run number passed in via URL.
 */
export function useGetSelectedRunFromRoute(
  queryOptions?: Omit<
    QueryObserverOptions<ReliabilityTestRun[], AxiosError, ReliabilityTestRun>,
    'queryKey' | 'queryFn'
  >
) {
  const {
    dependencyId,
    failureFlagName,
    reliabilityTestId,
    runNumber: runNumberFromSearchParams,
    serviceId
  } = useParams()

  const { currentTeamId: teamId } = useAuth()
  const runNumber = Number(runNumberFromSearchParams ?? 0)
  const isEnabled = Boolean(reliabilityTestId && !isNaN(runNumber))

  const optionalQueryKeyParams = getOptionalServiceQueryKeyParams(
    dependencyId,
    failureFlagName
  )

  const selectCallback = useCallback(
    (data: ReliabilityTestRun[]) => {
      if (isNaN(runNumber))
        throw new AxiosError('encountered an unexpected runNumber type')

      const selectedRun = selectReliabilityTestRunByNumber(data, runNumber)

      if (!selectedRun)
        throw new AxiosError('Run number not found in fetched runs')

      return selectedRun
    },
    [runNumber]
  )

  return useQuery<ReliabilityTestRun[], AxiosError, ReliabilityTestRun>({
    ...queryOptions,
    enabled: (queryOptions?.enabled ?? true) && isEnabled,
    queryFn: fetchReliabilityTestRunsById,
    select: selectCallback,
    ...(reliabilityTestId && {
      queryKey: reliabilityTestsQueryKeys.getReliabilityTestRunsById({
        ...optionalQueryKeyParams,
        reliabilityTestId,
        serviceId,
        teamId
      })
    })
  })
}
